import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { COMPANY } from "@/data/site";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/quote", label: "Free Quote" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 sm:h-32 lg:h-40">
          <Link
            to="/"
            data-testid="brand-logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src="/assets/logo.png"
              alt="Boston Express Plumbing and Heating LLC"
              className="h-20 sm:h-28 lg:h-36 w-auto"
            />
            <span className="sr-only">Boston Express Plumbing and Heating</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors ${
                    isActive
                      ? "text-[#0284C7]"
                      : "text-[#0F172A] hover:text-[#0284C7]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="navbar-call-now-link"
              className="inline-flex items-center gap-2 text-[#0284C7] font-semibold text-sm hover:text-[#0369A1]"
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} />
              {COMPANY.phone}
            </a>
            <Link to="/quote" data-testid="navbar-quote-button">
              <Button className="bg-[#0284C7] hover:bg-[#0369A1] text-white rounded-none font-semibold">
                Free Quote
              </Button>
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="navbar-mobile-call-link"
              aria-label="Call Now"
              className="inline-flex items-center justify-center h-10 w-10 bg-[#0284C7] text-white"
            >
              <Phone className="h-5 w-5" strokeWidth={1.75} />
            </a>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-trigger"
                  aria-label="Open menu"
                  className="inline-flex items-center justify-center h-10 w-10 border border-slate-200 text-[#0F172A]"
                >
                  <Menu className="h-5 w-5" strokeWidth={1.75} />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[85%] sm:w-[380px] bg-white"
                data-testid="mobile-menu-sheet"
              >
                <SheetHeader className="text-left">
                  <SheetTitle className="font-display text-xl">
                    Menu
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-8 flex flex-col gap-1">
                  {NAV_LINKS.map((link) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      end={link.to === "/"}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `py-3 px-2 border-b border-slate-100 text-base font-semibold ${
                          isActive ? "text-[#0284C7]" : "text-[#0F172A]"
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </div>
                <div className="mt-8 space-y-3">
                  <a
                    href={`tel:${COMPANY.phoneTel}`}
                    data-testid="mobile-menu-call-now-link"
                    className="block w-full text-center py-3 bg-[#0284C7] text-white font-semibold"
                  >
                    Call {COMPANY.phone}
                  </a>
                  <Link
                    to="/quote"
                    data-testid="mobile-menu-quote-link"
                    onClick={() => setOpen(false)}
                    className="block w-full text-center py-3 border-2 border-[#0F172A] text-[#0F172A] font-semibold"
                  >
                    Request Free Quote
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
